import { languages, params } from '@app/configurations';
import {
  add,
  addDays,
  areIntervalsOverlapping,
  differenceInCalendarDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  format,
  getDaysInMonth,
  getOverlappingDaysInIntervals,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  sub,
} from 'date-fns';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isWithinInterval from 'date-fns/isWithinInterval';
import { de, enGB, zhCN } from 'date-fns/locale';
import { getRawQueryParams } from './url.utilities';

export const weekdays = {
  [languages.ENGLISH]: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  [languages.GERMAN]: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  [languages.CHINESE]: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
};
export const months = {
  [languages.ENGLISH]: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  [languages.GERMAN]: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  [languages.CHINESE]: [
    '一月',
    '二月',
    '三月',
    '四月',
    '五月',
    '六月',
    '七月',
    '八月',
    '九月',
    '十月',
    '十一月',
    '十二月',
  ],
};
export const shortMonths = {
  [languages.ENGLISH]: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  [languages.GERMAN]: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  [languages.CHINESE]: months[languages.CHINESE],
};

export const shortWeekdays = {
  [languages.ENGLISH]: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  [languages.GERMAN]: ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'],
  [languages.CHINESE]: weekdays[languages.CHINESE],
};

export function isLastWeek(date: Date): boolean {
  const today = new Date();
  const dayDiffFromMonday = differenceInCalendarDays(date, today) - getDayOfWeek(today);

  return dayDiffFromMonday > 0 && dayDiffFromMonday <= 7;
}

export function isFirstDayofMonth(date: Date): boolean {
  return date.getDate() === 1;
}

export function isFirstDayOfQuarter(date) {
  return format(startOfQuarter(date), 'MMddyyyy') === format(date, 'MMddyyyy');
}
export function isLastDayOfQuarter(date) {
  return format(endOfQuarter(date), 'MMddyyyy') === format(date, 'MMddyyyy');
}

export function isLastMonth(date: Date): boolean {
  return isSameMonth(date, sub(new Date(), { months: 1 }));
}

export function isLastYear(date: Date): boolean {
  return new Date().getFullYear() - date.getFullYear() === 1;
}

export function getDayOfWeek(date: Date): number {
  // monday = 0
  return (date.getDate() - 1) % 7;
}

export function dateRangeOverlaps(dates) {
  const intervals = dates.map((d) => ({ start: d[0], end: d[1], diff: differenceInCalendarDays(d[1], d[0]) }));
  const isOverlapping = areIntervalsOverlapping(intervals[0], intervals[1], { inclusive: true });
  const daysOverlapped = getOverlappingDaysInIntervals(intervals[0], intervals[1]);
  if (isOverlapping) return !!intervals.find(({ diff }) => daysOverlapped === diff);
}

export function IsCurrentDayinlast3Days([start, end]: Date[]) {
  return Last3Days().some(
    (date) => (isSameDay(date, start) || isAfter(date, start)) && (isSameDay(date, end) || isBefore(date, end)),
  );
}
export function Last3Days() {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  return [addDays(today, 0), addDays(today, -1), addDays(today, -2)];
}

export function Last1Year(stringFormat = true) {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, 0), addDays(today, -365)];

  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function Last3Months(stringFormat = true) {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, 0), addDays(today, -90)];

  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}

/** Date range Jan 1, 2026 – Mar 4, 2026 (inclusive). */
export function getDateRangeJan1ToFeb28_2026(range): Date[] {
  const start = startOfDay(range[0]);
  const end = endOfDay(range[1]);

  return [start, end];
}

export function isDateInRange(date: Date | string, rangeStart: Date, rangeEnd: Date): boolean {
  const d = typeof date === 'string' ? GetDateWithoutTimeZone(new Date(date)) : GetDateWithoutTimeZone(date);
  const start = startOfDay(rangeStart);
  const end = endOfDay(rangeEnd);
  return isWithinInterval(d, { start, end });
}

export function excludeItemsByGivenDateRange(items, range) {
  if (range.length !== 2) return items;
  const [start, end] = getDateRangeJan1ToFeb28_2026(range);
  return items.filter(({ timestamp }) => !isDateInRange(timestamp, start, end));
}

export function getDateRangeLast30Days(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, -30), endOfDay(addDays(today, -1))];

  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}

export function getDateRangeYesterday(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, -1), addDays(today, -1)];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeLastWeek(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(startOfWeek(addDays(today, -1)), -6), startOfWeek(addDays(today, -1))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeBeforeLastWeek(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(startOfWeek(addDays(today, -1)), -13), startOfWeek(addDays(today, -8))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeLastMonth(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [
    startOfMonth(addDays(startOfMonth(today), -1)),
    GetDateWithoutTimeZone(endOfMonth(addDays(startOfMonth(today), -1))),
  ];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeBeforeLastMonth(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const tmp = addDays(startOfMonth(addDays(startOfMonth(today), -1)), -1);
  const dates = [startOfMonth(tmp), GetDateWithoutTimeZone(endOfMonth(tmp))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeLast7Days(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, -7), endOfDay(addDays(today, -1))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeBeforeLast7Days(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, -14), endOfDay(addDays(today, -(7 + 1)))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}
export function getDateRangeBeforeLast30Days(stringFormat = true): string[] | Date[] {
  const today = startOfDay(GetDateWithoutTimeZone(null));
  const dates = [addDays(today, -(60 + 1)), endOfDay(addDays(today, -(30 + 1)))];
  if (stringFormat) {
    return dates.map((d) => format(d, 'yyyy-MM-dd'));
  }
  return dates;
}

export function getPrevMonthToDate(dates: [Date, Date]): [Date, Date] {
  const tmpPrevMonth = sub(dates[0], { months: 1 });
  const days = Math.min(Math.abs(differenceInDays(dates[1], dates[0])), getDaysInMonth(tmpPrevMonth));
  return [startOfMonth(tmpPrevMonth), endOfDay(add(tmpPrevMonth, { days }))];
}

export function GetDateRangeWithoutTimeZone(dates: string | undefined, formatDate = false): string[] | Date[] {
  const validDate = dates?.split(',') && dates.split(',').length == 2;

  const newDateRange = validDate ? dates.split(',') : getDateRangeLast30Days();

  if (formatDate) {
    return (<string[]>newDateRange).map((d) => format(GetDateWithoutTimeZone(new Date(d)), 'yyyy-MM-dd'));
  }
  return (<string[]>newDateRange).map((d) => GetDateWithoutTimeZone(new Date(d)));
}

export function GetDateWithoutTimeZone(d?: Date | string): Date {
  if (!d) d = new Date();
  if (typeof d === 'string') d = new Date(d);
  return startOfDay(new Date(d.valueOf() + d.getTimezoneOffset() * 60 * 1000));
}

export function SplitDatesFromUrl(dateString: string, dateFormat = false): Date[] {
  if (!dateString) return <Date[]>getDateRangeLast30Days(!dateFormat);

  return dateString.split(',').map((d: string) => GetDateWithoutTimeZone(new Date(d)));
}

export function FormatDatesToString(dateString: string) {
  if (!dateString) return '';

  const [start, end] = SplitDatesFromUrl(dateString);
  return LocalizedDate(start, 'd MMM yyyy') + ' - ' + LocalizedDate(end, 'd MMM yyyy');
}

export function FormatDateForURLParam(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

export function LocalizedDate(value: Date | string | number, formatPattern) {
  if (!value) return '-';
  if (typeof value === 'string') value = new Date(value);

  let locale = enGB;
  const storedLanguage = window.adspert_data?.user?.lang?.toUpperCase() || 'EN';

  if (storedLanguage === languages.GERMAN) locale = de;
  else if (storedLanguage === languages.CHINESE) locale = zhCN;
  try {
    return format(value, LocalizedDatePattern(formatPattern), { locale });
  } catch (error) {
    return '-';
  }
}

export function LocalizedMonthName(date, isShort) {
  const index = new Date(date).getMonth();
  const lang = window.adspert_data.user.lang?.toUpperCase() || 'EN';
  return isShort ? shortMonths[lang][index] : months[lang][index];
}

export function GetDaysCountFromRefiners(refiners: string[]): number {
  const dateStart = new Date(refiners.find(([key]) => key === 'filter_date_ge')[1]);
  const dateEnd = new Date(refiners.find(([key]) => key === 'filter_date_le')[1]);
  return differenceInCalendarDays(dateEnd, dateStart) + 1;
}

export function getTimeZoneOffsetHrs(): number {
  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();
  return -offsetMinutes / 60;
}

export function LocalizedDatePattern(formatPattern: string): string {
  if (window.adspert_data.user.lang.toLowerCase() !== languages.CHINESE.toLowerCase()) return formatPattern;
  const datePatternMap = {
    'EEE dd MMM, yyyy': 'yyyy年M月d日E',
    'yyyy-MM-dd': 'yyyy年M月d日',
    'd MMM yyyy': 'yyyy年M月d日',
    'd MMM, yyyy': 'yyyy年M月d日',
    'dd MMM, yyyy': 'yyyy年M月d日',
    'dd MMM yyyy': 'yyyy年M月d日',
    'MMM yy': 'yy年M月',
    'MM.yyyy': 'yy年M月',
    'd MMM': 'M月d日',
    'MMM, yyyy': 'yyyy年M月',
  };

  return datePatternMap[formatPattern] || formatPattern;
}

export function getNumberOfDays(dates = null): number {
  const [start, end] = dates || getRawQueryParams()[params.GLOBAL_FILTER_DATE_RANGE].split(',');
  return differenceInDays(new Date(end), new Date(start)) + 1;
}
